import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import axios from "axios";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import moment from "moment";
import crossImage from "../../images/singleinvoice/cross-red.svg";
import emailImg from "../../images/user/rey.svg";
import { useUserSuggestions } from "../User/userProfileQueries";
import CalendarIcon from '../ui/CalendarIcon';

const SUPER_ADMIN = 1;
const ADMIN = 2;
const USER = 3;
const PROJECT_MANAGER = 6;
const TEAM_MANAGER = 7;

const ranges = [
    {
        label: "Today",
        value: [new Date(), new Date()],
    },
    {
        label: "Tomorrow",
        value: [moment().add(1, "days").toDate(), moment().add(1, "days").toDate()],
    },
    {
        label: "Next 7 Days",
        value: [new Date(), moment().add(6, "days").toDate()],
    },
];

const EditEventModal = ({
    editEventModalOpen,
    setEditEventModalOpen,
    isSubmitLoading,
    setIsSubmitLoading,
    selectedEventForEdit,
    setSelectedEventForEdit,
    setSelectedEvents,
    setExpandedEventIndex,
    setModalIsOpen,
    eventName,
    setEventName,
    repeatType,
    setRepeatType,
    REPEAT_TYPES,
    eventBannerFile,
    eventDate,
    setEventDate,
    eventEndDate,
    setEventEndDate,
    eventStartTime,
    setEventStartTime,
    eventEndTime,
    setEventEndTime,
    eventDescription,
    setEventDescription,
    eventLocation,
    setEventLocation,
    user,
    eventOrganizer,
    setEventOrganizer,
    userProfiles,
    userProfileLoading,
    eventAttendees,
    setEventAttendees,
    externalAttendees,
    setExternalAttendees,
    isVirtual,
    setIsVirtual,
    eventMeetingLink,
    setEventMeetingLink,
    refetchEvents,
    token,
}) => {
    const [dateRangeType, setDateRangeType] = useState("multiple");
    const [repeatTypeDropdownOpen, setRepeatTypeDropdownOpen] = useState(false);
    const [organizerDropdownOpen, setOrganizerDropdownOpen] = useState(false);
    const [attendeeInput, setAttendeeInput] = useState('');
    const [attendeeSuggestions, setAttendeeSuggestions] = useState([]);
    const [updateScope, setUpdateScope] = useState('this');

    const [eventNameIsValid, setEventNameIsValid] = useState(false);
    const [repeatTypeIsValid, setRepeatTypeIsValid] = useState(false);
    const [eventDateIsValid, setEventDateIsValid] = useState(false);
    const [eventEndDateIsValid, setEventEndDateIsValid] = useState(false);
    const [eventEndTimeIsValid, setEventEndTimeIsValid] = useState(false);
    const [eventStartTimeIsValid, setEventStartTimeIsValid] = useState(false);

    const eventNameRef = useRef(null);
    const repeatTypeRef = useRef(null);
    const eventDateRef = useRef(null);
    const eventEndDateRef = useRef(null);
    const eventStartTimeRef = useRef(null);
    const eventEndTimeRef = useRef(null);
    const organizerRef = useRef(null);
    const eventMeetingLinkRef = useRef(null);
    const prevUserSuggestionsRef = useRef(null);
    const prevUserProfilesRef = useRef(null);
    const prevEventOrganizerRef = useRef(null);
    const prevAttendeeInputRef = useRef(null);
    const prevEventAttendeesRef = useRef(null);
    const prevExternalAttendeesRef = useRef(null);

    const { appSettings } = useSelector((state) => state.appSettings);

    // Fetch user suggestions for attendee selection
    const { data: userSuggestions = [], isLoading: userSuggestionsLoading } = useUserSuggestions(token);

    useEffect(() => {
        // Check if values have actually changed
        const userSuggestionsString = JSON.stringify(userSuggestions);
        const userProfilesString = JSON.stringify(userProfiles);
        const eventOrganizerChanged = prevEventOrganizerRef.current !== eventOrganizer;
        const userSuggestionsChanged = prevUserSuggestionsRef.current !== userSuggestionsString;
        const userProfilesChanged = prevUserProfilesRef.current !== userProfilesString;
        
        // Only proceed if something actually changed
        if (!eventOrganizerChanged && !userSuggestionsChanged && !userProfilesChanged && prevEventOrganizerRef.current !== null) {
            return;
        }
        
        // Update refs
        prevEventOrganizerRef.current = eventOrganizer;
        prevUserSuggestionsRef.current = userSuggestionsString;
        prevUserProfilesRef.current = userProfilesString;
        
        if (eventOrganizer) {
            // Use userSuggestions for organizer lookup since it contains all users
            const organizer = (Array.isArray(userSuggestions) ? userSuggestions.find((user) => user._id === eventOrganizer) : null) 
                || (Array.isArray(userProfiles) ? userProfiles.find((user) => user._id === eventOrganizer) : null);
            if (organizer) {
                setEventAttendees((prevAttendees) => {
                    // Only add organizer if not already in attendees
                    if (!prevAttendees.some((a) => a._id === eventOrganizer)) {
                        return [organizer, ...prevAttendees];
                    }
                    return prevAttendees;
                });
            }
        }
    }, [eventOrganizer, userSuggestions, userProfiles]);

    useEffect(() => {
        // Check if values have actually changed
        const userSuggestionsString = JSON.stringify(userSuggestions);
        const eventAttendeesString = JSON.stringify(eventAttendees);
        const externalAttendeesString = JSON.stringify(externalAttendees);
        
        const attendeeInputChanged = prevAttendeeInputRef.current !== attendeeInput;
        const userSuggestionsChanged = prevUserSuggestionsRef.current !== userSuggestionsString;
        const eventAttendeesChanged = prevEventAttendeesRef.current !== eventAttendeesString;
        const externalAttendeesChanged = prevExternalAttendeesRef.current !== externalAttendeesString;
        
        // Only proceed if something actually changed
        if (!attendeeInputChanged && !userSuggestionsChanged && !eventAttendeesChanged && !externalAttendeesChanged && prevAttendeeInputRef.current !== null) {
            return;
        }
        
        // Update refs
        prevAttendeeInputRef.current = attendeeInput;
        prevUserSuggestionsRef.current = userSuggestionsString;
        prevEventAttendeesRef.current = eventAttendeesString;
        prevExternalAttendeesRef.current = externalAttendeesString;
        
        if (!attendeeInput) {
            setAttendeeSuggestions([]);
            return;
        }

        const selectedInternalIds = eventAttendees.map(a => a._id);
        const selectedExternalEmails = externalAttendees.map(a => a.email.toLowerCase());

        const filtered = Array.isArray(userSuggestions) ? userSuggestions.filter((user) =>
            user.email.toLowerCase().includes(attendeeInput.toLowerCase()) &&
            !selectedInternalIds.includes(user._id)
        ) : [];

        const exactMatch = Array.isArray(userSuggestions) ? userSuggestions.find(
            (user) => user.email.toLowerCase() === attendeeInput.toLowerCase()
        ) : null;

        if (!exactMatch &&
            attendeeInput.includes('@') &&
            attendeeInput.includes('.') &&
            !selectedExternalEmails.includes(attendeeInput.toLowerCase())) {
            filtered.push({
                _id: null,
                email: attendeeInput,
                name: 'External Email',
                external: true,
            });
        }

        setAttendeeSuggestions(filtered);
    }, [attendeeInput, userSuggestions, eventAttendees, externalAttendees]);

    const handleDateSelect = (date) => {
        setEventDate(date);
        setEventDateIsValid(false);
    };

    const handleToggleOrganizerDropdown = () => {
        setOrganizerDropdownOpen(!organizerDropdownOpen);
    };

    const handleSelectAttendee = (attendee) => {
        if (attendee.external) {
            if (!externalAttendees.some((a) => a.email === attendee.email)) {
                setExternalAttendees([...externalAttendees, {
                    email: attendee.email,
                    name: null,
                    status: 'pending',
                    reason: null,
                    response_date: null,
                    additional_notes: null,
                }]);
            }
        } else {
            if (!eventAttendees.some((a) => a._id === attendee._id)) {
                console.log("attendee", attendee);
                setEventAttendees([...eventAttendees, attendee]);
            }
        }
        setAttendeeInput('');
        setAttendeeSuggestions([]);
    };

    const handleRemoveAttendee = (idOrEmail, isExternal) => {
        if (!isExternal && idOrEmail === eventOrganizer?._id) {
            return;
        }
        if (isExternal) {
            setExternalAttendees(externalAttendees.filter((a) => a.email !== idOrEmail));
        } else {
            setEventAttendees(eventAttendees.filter((a) => a._id !== idOrEmail));
        }
    };

    const handleToggleRepeatTypeDropdown = () => {
        setRepeatTypeDropdownOpen(!repeatTypeDropdownOpen);
    };

    const handleEditEventSubmit = async (e) => {
        e.preventDefault();

        setEventNameIsValid(false);
        setEventDateIsValid(false);
        setEventStartTimeIsValid(false);
        setEventEndTimeIsValid(false);
        setRepeatTypeIsValid(false);
        setEventEndDateIsValid(false);

        let isValid = true;

        if (!eventName) {
            setEventNameIsValid(true);
            eventNameRef.current?.focus();
            isValid = false;
        }
        if (!eventDate) {
            setEventDateIsValid(true);
            eventDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (!repeatType) {
            setRepeatTypeIsValid(true);
            repeatTypeRef.current?.focus();
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && !eventEndDate) {
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && eventDate && eventEndDate && moment(eventEndDate).isBefore(moment(eventDate), "day")) {
            toast.error("End date must be on or after the event date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.focus();
            isValid = false;
        }
        if (repeatType !== "all_day") {
            if (!eventStartTime) {
                setEventStartTimeIsValid(true);
                eventStartTimeRef.current?.focus();
                isValid = false;
            }
            if (!eventEndTime) {
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
            if (eventStartTime && eventEndTime && moment(eventStartTime, "HH:mm").isSameOrAfter(moment(eventEndTime, "HH:mm"))) {
                toast.error("Start time must be before end time!", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setEventStartTimeIsValid(true);
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
        }

        if (!isValid) {
            toast.error("Please fill all required fields!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        try {
            setIsSubmitLoading(true);
            const requestData = {
                event_name: eventName,
                event_date: moment(eventDate).format("YYYY-MM-DD"),
                end_date:
                    ["daily", "weekly", "monthly"].includes(repeatType) && eventEndDate
                        ? moment(eventEndDate).format("YYYY-MM-DD")
                        : null,
                start_date_time:
                    eventStartTime && eventEndTime && repeatType !== "all_day"
                        ? moment(eventStartTime).toISOString()
                        : null,
                end_date_time:
                    eventStartTime && eventEndTime && repeatType !== "all_day"
                        ? moment(eventEndTime).toISOString()
                        : null,
                event_description: eventDescription || null,
                event_location: eventLocation || null,
                event_organizer: eventOrganizer,
                // event_attendees: eventAttendees.length > 0 ? eventAttendees.map((a) => a._id) : [],
                event_attendees: eventAttendees.length > 0 ? eventAttendees.map((a) => ({
                    user: a._id,
                    status: 'pending',
                    reason: null,
                    response_date: null,
                    additional_notes: null
                })) : [],
                external_attendees: externalAttendees.length > 0 ? externalAttendees.map((a) => ({
                    email: a.email,
                    name: a.name || null,
                    status: 'pending',
                    reason: null,
                    response_date: null,
                    additional_notes: null
                })) : [],
                event_is_virtual: isVirtual,
                event_meeting_link: isVirtual && eventMeetingLink ? eventMeetingLink : null,
                repeat_type: repeatType,
                update_scope: updateScope,
            };

            let config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            let payload;
            if (eventBannerFile) {
                const formData = new FormData();
                Object.entries(requestData).forEach(([key, value]) => {
                    if (key === "event_attendees" && Array.isArray(value)) {
                        // Append each attendee ID individually
                        // value.forEach((attendeeId, index) => {
                        //     formData.append(`event_attendees[${index}]`, attendeeId);
                        // });
                        value.forEach((attendee, index) => {
                            formData.append(`event_attendees[${index}][user]`, attendee.user);
                            formData.append(`event_attendees[${index}][status]`, attendee.status);
                            if (attendee.reason) formData.append(`event_attendees[${index}][reason]`, attendee.reason);
                            if (attendee.response_date) formData.append(`event_attendees[${index}][response_date]`, attendee.response_date);
                            if (attendee.additional_notes) formData.append(`event_attendees[${index}][additional_notes]`, attendee.additional_notes);
                        });
                    } else if (key === "external_attendees" && Array.isArray(value)) {
                        value.forEach((attendee, index) => {
                            formData.append(`external_attendees[${index}][email]`, attendee.email);
                            if (attendee.name) formData.append(`external_attendees[${index}][name]`, attendee.name);
                            formData.append(`external_attendees[${index}][status]`, attendee.status);
                            if (attendee.reason) formData.append(`external_attendees[${index}][reason]`, attendee.reason);
                            if (attendee.response_date) formData.append(`external_attendees[${index}][response_date]`, attendee.response_date);
                            if (attendee.additional_notes) formData.append(`external_attendees[${index}][additional_notes]`, attendee.additional_notes);
                        });
                    } else if (value !== null) {
                        formData.append(key, value);
                    }
                });
                formData.append("event_banner", eventBannerFile);
                payload = formData;
                config.headers["Content-Type"] = "multipart/form-data";
            } else {
                payload = requestData;
            }

            await axios.put(`/api/events/update/${selectedEventForEdit._id}`, payload, config);

            global.config.activityLog(
                window.location.href,
                "Event Update",
                `Event "${eventName}" updated successfully`
            );
            toast.success("Event updated successfully", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            refetchEvents();
            handleCloseEditModal();
            setModalIsOpen(false);
            setExpandedEventIndex(null);
            setSelectedEvents([]);
        } catch (error) {
            console.error("Error updating event:", error);
            global.config.slackMessage(error.toString());

            if (error.response?.data?.status === "error") {
                toast.error(error.response.data.message, {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                toast.error("Something went wrong", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        if (!isSubmitLoading) {
            setEditEventModalOpen(false);
            setSelectedEventForEdit(null);
            // setDateRange([null, null]);
            setEventDate(null);
            setEventEndDate(null);
            setDateRangeType("single");
            setEventStartTime("");
            setEventEndTime("");
            setEventName("");
            setEventDescription("");
            setEventLocation("");
            setEventOrganizer(user._id);
            setEventAttendees([]);
            // setEventBanner(null);
            setIsVirtual(false);
            setEventMeetingLink("");
            setRepeatType("does_not_repeat");
            setEventNameIsValid(false);
            setEventDateIsValid(false);
            setEventStartTimeIsValid(false);
            setEventEndTimeIsValid(false);
            setRepeatTypeIsValid(false);
            setOrganizerDropdownOpen(false);
            setRepeatTypeDropdownOpen(false);
            setUpdateScope('this');
            document.body.classList.remove("ReactModal__Body--open");
        }
    };

    const handleClickOutsideDropdown = (event) => {
        const refs = {
            repeatType: repeatTypeRef.current,
            organizer: organizerRef.current,
        };

        // Log any null refs for debugging
        Object.entries(refs).forEach(([key, ref]) => {
            if (!ref) {
                console.warn(`Ref ${key} is null`);
            }
        });

        try {
            if (refs.repeatType && !refs.repeatType.contains(event.target)) {
                setRepeatTypeDropdownOpen(false);
            }
            if (refs.organizer && !refs.organizer.contains(event.target)) {
                setOrganizerDropdownOpen(false);
            }
        } catch (err) {
            console.error('Error in handleClickOutsideDropdown:', err);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined' || !document) {
            return;
        }

        document.addEventListener('click', handleClickOutsideDropdown);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, []);

    return (
        <Modal
            className="client-modal sprint-modal time-off-close calendar-event-create calendar-event-edit"
            isOpen={editEventModalOpen}
            onRequestClose={!isSubmitLoading ? handleCloseEditModal : undefined}
            contentLabel="Edit Event"
            style={{
                content: {
                    height: "41%",
                    width: "30%",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                },
            }}
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="modal timeoff-modal">
                        <div>
                            <div className="modal-client-head">
                                <h2>Edit Event</h2>
                                <div className="modal-close-invite outter-modal-main">
                                    <a onClick={handleCloseEditModal} disabled={isSubmitLoading}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <div className="form-field-client-outer">
                                <form onSubmit={handleEditEventSubmit}>
                                    <div className="form-field-client">
                                        <label htmlFor="eventName" className="form-label">
                                            Event Name *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="eventName"
                                                type="text"
                                                value={eventName}
                                                onChange={(e) => setEventName(e.target.value)}
                                                className={eventNameIsValid ? "input-validation-error form-input" : "form-input"}
                                                ref={eventNameRef}
                                            />
                                            {eventNameIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Event name is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="repeatType" className="form-label">
                                            Repeat Type *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={repeatTypeRef} className={`cursor ${repeatTypeDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="repeat-type-select"
                                                    onClick={handleToggleRepeatTypeDropdown}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${repeatTypeIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {repeatType ? REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label : 'Select'}
                                                </span>
                                                {repeatTypeDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {REPEAT_TYPES.map((rt) => (
                                                                <li
                                                                    key={rt.value}
                                                                    className={repeatType === rt.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setRepeatType(rt.value);
                                                                        setRepeatTypeDropdownOpen(false);
                                                                        if (rt.value === "all_day") {
                                                                            setEventStartTime("");
                                                                            setEventEndTime("");
                                                                        }
                                                                        if (!["daily", "weekly", "monthly"].includes(rt.value)) {
                                                                            setEventEndDate(null);
                                                                        }
                                                                    }}
                                                                >
                                                                    {rt.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {repeatTypeIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Repeat type is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {["daily", "weekly", "monthly"].includes(selectedEventForEdit?.repeatType) && (
                                        <div className="form-field-client scope-sec">
                                            <label className="form-label">Update Scope *</label>
                                            <div className="inner-sec-cal-form">
                                                <div className="radio-group">
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this"
                                                            checked={updateScope === "this"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This Event
                                                    </label>
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this_and_following"
                                                            checked={updateScope === "this_and_following"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This and Following Events
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field-client">
                                        <label htmlFor="eventDate" className="form-label">
                                            {["daily", "weekly", "monthly"].includes(repeatType) ? "Start Date *" : "Event Date *"}
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div className="date-picker-outer date-picker-outer-all">
                                                <div className="custoom-icon-calender custoom-icon-calender-all">
                                                    <div ref={eventDateRef}>
                                                        <DatePicker
                                                            oneTap
                                                            format="dd/MM/yyyy"
                                                            placeholder="Select Date"
                                                            placement="topEnd"
                                                            ranges={ranges}
                                                            className="project-calender time-off-cal"
                                                            value={eventDate}
                                                            onChange={handleDateSelect}
                                                            disabled={true} // Read-only effect achieved via oneTap and styling
                                                            isoWeek={appSettings.weekStartDay === 'Monday'}
                                                        // shouldDisableDate={(date) => moment(date).isBefore(moment(), 'day')} // Disable past dates
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {eventDateIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Select the event date range
                                                </span>
                                            )}
                                        </div>
                                    </div>


                                    {["daily", "weekly", "monthly"].includes(repeatType) && (
                                        <div className="form-field-client">
                                            <label htmlFor="eventEndDate" className="form-label">
                                                End Date *
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all">
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={eventEndDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select End Date"
                                                                placement="topEnd"
                                                                ranges={ranges}
                                                                className="project-calender time-off-cal"
                                                                value={eventEndDate}
                                                                onChange={(date) => setEventEndDate(date)}
                                                                isoWeek={appSettings.weekStartDay === 'Monday'}
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(eventDate), 'day')}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {eventEndDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        Select the event end date
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {dateRangeType === "single" && repeatType !== "all_day" && (
                                        <div className="form-field-client radio-sec">
                                            <div className="form-field-client">
                                                <label htmlFor="eventStartTime" className="form-label">
                                                    Start Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="startTime"
                                                        type="time"
                                                        value={eventStartTime ? moment(eventStartTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const updatedStart = moment(eventStartTime || new Date())
                                                                .set({ hour: hours, minute: minutes })
                                                                .toDate();
                                                            setEventStartTime(updatedStart);
                                                        }}
                                                        className={eventStartTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventStartTimeRef}
                                                    />
                                                    {eventStartTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid start time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-field-client">
                                                <label htmlFor="eventEndTime" className="form-label">
                                                    End Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="endTime"
                                                        type="time"
                                                        value={eventEndTime ? moment(eventEndTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const updatedEnd = moment(eventEndTime || new Date())
                                                                .set({ hour: hours, minute: minutes })
                                                                .toDate();
                                                            setEventEndTime(updatedEnd);
                                                        }}
                                                        className={eventEndTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventEndTimeRef}
                                                    />
                                                    {eventEndTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid end time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="location" className="form-label">
                                                Location
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <input
                                                    id="location"
                                                    type="text"
                                                    value={eventLocation}
                                                    onChange={(e) => setEventLocation(e.target.value)}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client is-virtual-vent">
                                        <label htmlFor="isVirtual" className="form-label">
                                            Virtual Event
                                        </label>
                                        <input
                                            id="isVirtual"
                                            type="checkbox"
                                            checked={isVirtual}
                                            onChange={(e) => setIsVirtual(e.target.checked)}
                                            className="form-checkbox"
                                        />
                                    </div>
                                    {isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="meetingLink" className="form-label">
                                                Meeting Link
                                            </label>
                                            <input
                                                id="meetingLink"
                                                type="text"
                                                value={eventMeetingLink}
                                                onChange={(e) => setEventMeetingLink(e.target.value)}
                                                className="form-input"
                                                ref={eventMeetingLinkRef}
                                            />
                                        </div>
                                    )}
                                    {(user.userType === SUPER_ADMIN || user.userType === ADMIN) && (
                                        <div className="form-field-client">
                                            <label htmlFor="organizer" className="form-label">
                                                Organizer
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div ref={organizerRef} className={`cursor ${organizerDropdownOpen ? 'open' : ''}`}>
                                                    <span
                                                        id="organizer-select"
                                                        onClick={handleToggleOrganizerDropdown}
                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                    >
                                                        {eventOrganizer
                                                            ? (() => {
                                                                const organizerUser = userSuggestions.find((u) => u._id === eventOrganizer) || userProfiles.find((u) => u._id === eventOrganizer);
                                                                return organizerUser?.first_name
                                                                    ? `${organizerUser.first_name} ${organizerUser.last_name || ''}`
                                                                    : `${organizerUser?.company_name || "Current User"}`;
                                                            })()
                                                            : 'Select Organizer'}
                                                    </span>
                                                    {organizerDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            {userProfileLoading ? (
                                                                <ul className="client-list">
                                                                    {Array.from({ length: 5 }).map((_, idx) => (
                                                                        <li key={idx} style={{ padding: '8px 12px', cursor: 'default' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                                <div
                                                                                    style={{
                                                                                        width: '20px',
                                                                                        height: '20px',
                                                                                        borderRadius: '50%',
                                                                                        backgroundColor: '#e5e7eb',
                                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                                    }}
                                                                                />
                                                                                <div
                                                                                    style={{
                                                                                        width: `${120 + idx * 10}px`,
                                                                                        height: '18px',
                                                                                        backgroundColor: '#e5e7eb',
                                                                                        borderRadius: '4px',
                                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <ul className="client-list">
                                                                    {userProfiles
                                                                        .slice()
                                                                        .sort((a, b) => {
                                                                            const nameA = a.first_name
                                                                                ? `${a.first_name} ${a.last_name}`
                                                                                : a.company_name;
                                                                            const nameB = b.first_name
                                                                                ? `${b.first_name} ${b.last_name}`
                                                                                : b.company_name;
                                                                            return nameA.localeCompare(nameB);
                                                                        })
                                                                        .map((item) => (
                                                                            <li
                                                                                key={item._id}
                                                                                className={eventOrganizer === item._id ? 'selected-item' : ''}
                                                                                onClick={() => {
                                                                                    setEventOrganizer(item._id);
                                                                                    setOrganizerDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                {item.first_name
                                                                                    ? `${item.first_name} ${item.last_name}`
                                                                                    : `${item.company_name || ''} (Company)`}
                                                                            </li>
                                                                        ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client guest-input-wrapper">
                                        <label className="form-label">Guests</label>
                                        <div className="suggession-user">
                                            <input
                                                type="text"
                                                value={attendeeInput}
                                                onChange={(e) => setAttendeeInput(e.target.value)}
                                                placeholder="Add guests"
                                                className="form-input"
                                                disabled={userSuggestionsLoading}
                                            />
                                            {userSuggestionsLoading ? (
                                                <ul className="suggestion-dropdown">
                                                    {Array.from({ length: 3 }).map((_, idx) => (
                                                        <li key={idx} style={{ padding: '8px 12px', cursor: 'default' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div
                                                                    style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#e5e7eb',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        width: `${150 + idx * 20}px`,
                                                                        height: '18px',
                                                                        backgroundColor: '#e5e7eb',
                                                                        borderRadius: '4px',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                attendeeSuggestions.length > 0 && (
                                                    <ul className="suggestion-dropdown">
                                                        {attendeeSuggestions.map((user, idx) => (
                                                            <li key={user._id || `external-${idx}`} onClick={() => handleSelectAttendee(user)}>
                                                                <span className="email-icon">
                                                                    <img
                                                                        src={`/uploads/lowProfileImage/${user.profile_image}`}
                                                                        alt="Email"
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = "/uploads/lowProfileImage/user_default.png";
                                                                        }}
                                                                    />
                                                                </span>
                                                                <span>{user.email}</span>
                                                                {user.external && <em> (external)</em>}
                                                                {user._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )
                                            )}
                                        </div>
                                    </div>
                                    {(eventAttendees.length > 0 || externalAttendees.length > 0) && (
                                        <div className="form-field-client guest-input-wrapper hide-it">
                                            <label className="form-label"></label>
                                            <div className="selected-guests">
                                                {eventAttendees.map((attendee) => (
                                                    <div key={attendee._id} className="guest-chip">
                                                        <span className={`email-icon ${attendee._id === eventOrganizer ? 'organizer-icon' : ''}`}>
                                                            <img
                                                                src={`/uploads/lowProfileImage/${attendee.profile_image}`}
                                                                alt="Email"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/uploads/lowProfileImage/user_default.png";
                                                                }}
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span className={`attendee-name ${attendee._id === eventOrganizer ? 'organizer-name' : ''}`}>
                                                                {attendee.first_name ? `${attendee.first_name} ${attendee.last_name || ''}` : attendee.company_name || attendee.email}
                                                                {attendee._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </span>
                                                            {attendee._id !== eventOrganizer && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAttendee(attendee._id, false)}
                                                                    className=""
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                                {externalAttendees.map((attendee, i) => (
                                                    <div key={`external-${i}`} className="guest-chip">
                                                        <span className="email-icon">
                                                            <img
                                                                src={`/uploads/lowProfileImage/${attendee.profile_image}`}
                                                                alt="Email"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/uploads/lowProfileImage/user_default.png";
                                                                }}
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span>{attendee.email} (external)</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveAttendee(attendee.email, true)}
                                                                className=""
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client">
                                        <label htmlFor="description" className="form-label">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            className="form-textarea"
                                            rows="3"
                                            value={eventDescription}
                                            onChange={(e) => setEventDescription(e.target.value)}
                                        ></textarea>
                                    </div>
                                    <button type="submit" disabled={isSubmitLoading} className="submit-client submit-client-cal">
                                        {isSubmitLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <ButtonSkeleton width="100px" height="18px" />
                                            </div>
                                        ) : (
                                            "Update Event"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditEventModal;